import { getFieldErrorKeys } from "helpers/getFieldErrorKeys";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "store";
import { handleUpdateFields } from "store/liveOps/tournaments/slice";

import InnerInput from "components/inputs/InnerInput/InnerInput";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import ActionBtn from "components/buttons/ActionBtn";

import binIcon from "assets/icons/bin-icon.svg";
import rewardIcon from "assets/icons/reward-icon.png";

import "../../tournaments/LiveOpsTournamentsEditModal.module.scss"

type Props = {
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  resetErrors: () => void;
  readOnly?: boolean;
}

const TournamentsRewardsTab: React.FC<Props> = ({ readOnly, fieldErrors, clearFieldError, resetErrors }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const payload = useAppSelector((state) => state.tournaments.payload);

  const [optionsBonuses, setOptionsBonuses] = useState<
    { value: string; label: string }[]
  >([]);

  const rewards = payload?.rewards || [];

  const errorsFor = (index: number, field: string) =>
    getFieldErrorKeys(fieldErrors, field, `rewards.${index}.${field}`);

  const updateReward = (place: string, field: string, value: any) => {
    errorsFor(rewards.findIndex((reward) => reward.place === place), field).forEach(clearFieldError);
    const updatedRewards = rewards.map((reward) =>
      reward.place === place ? { ...reward, [field]: value } : reward
    );
    // @ts-ignore
    dispatch(handleUpdateFields({ ...payload, rewards: updatedRewards }));
  };

  const addReward = () => {
    resetErrors();
    const maxPlace =
      rewards.length > 0
        ? Math.max(...rewards.map((r) => parseInt(r.place ?? "0", 10)))
        : 0;
    const newPlace = (maxPlace + 1).toString();

    const newReward = {
      place: newPlace,
      coins: "",
      loyaltyPoints: "",
      rankingPoints: "",
      bonusId: "",
    };
    dispatch(
      // @ts-ignore
      handleUpdateFields({ ...payload, rewards: [...rewards, newReward] })
    );
  };

  const removeReward = (place: string) => {
    resetErrors();
    const updatedRewards = rewards.filter((reward) => reward.place !== place);
    // @ts-ignore
    dispatch(handleUpdateFields({ ...payload, rewards: updatedRewards }));
  };

  return (
    <div className="tournaments-rewards-wrap">
      <div className="bonus-rewards-header">
        <ActionBtn
          txt={t("addReward")}
          handleClick={addReward}
          isWithText
          disabled={readOnly}
        />
      </div>

      <div className="bonus-rewards-list">
        {rewards.map((reward, idx) => (
          <div className="bonus-rewards-list--item" key={reward.place}>
            <div className="left">
              <img src={rewardIcon} alt="reward" />
              <div className="title">{`#${reward.place}`}</div>
            </div>

            <div className="right">
              <div className="inputs-row">
                <InnerInput
                  hasError={errorsFor(idx, "coins").length > 0}
                  value={reward.coins ?? ""}
                  placeholder={t("coins")}
                  label={t("coins")}
                  type="number"
                  onChange={(val) => updateReward(reward.place, "coins", val)}
                  id="reward-coins"
                  disabled={readOnly}
                />
                <InnerInput
                  hasError={errorsFor(idx, "loyaltyPoints").length > 0}
                  value={reward.loyaltyPoints ?? ""}
                  placeholder={t("loyaltyPoints")}
                  label={t("loyaltyPoints")}
                  type="number"
                  onChange={(val) =>
                    updateReward(reward.place, "loyaltyPoints", val)
                  }
                  id="reward-loyaltyPoints"
                  disabled={readOnly}
                />
                <InnerInput
                  hasError={errorsFor(idx, "rankingPoints").length > 0}
                  value={reward.rankingPoints ?? ""}
                  placeholder={t("rankingPoints")}
                  label={t("rankingPoints")}
                  type="number"
                  onChange={(val) =>
                    updateReward(reward.place, "rankingPoints", val)
                  }
                  id="reward-rankingPoints"
                  disabled={readOnly}
                />
                <DefaultDropdown
                  placeholder={t("bonus")}
                  label={t("bonus")}
                  options={optionsBonuses}
                  hasError={errorsFor(idx, "bonusId").length > 0}
                  value={reward.bonusId ?? ""}
                  onSelect={(val) =>
                    updateReward(reward.place, "bonusId", val as string)
                  }
                  disabled={readOnly}
                />
              </div>

              {!readOnly && (
                <img
                  src={binIcon}
                  alt="delete"
                  className={`delete-icon ${idx === rewards.length - 1 ? "is-active" : ""}`}
                  onClick={() => removeReward(reward.place)}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TournamentsRewardsTab;
